// src/App.js

import React, { Suspense, useContext, useEffect, memo, useRef, useState } from 'react';
import './App.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import { MiningContext } from './context/MiningContext';
import { WalletContext } from './context/WalletContext';
import MainProvider from './context/MainContext';
import { CircularProgress, Typography, Box, Tabs, Tab } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import ErrorBoundary from './components/ErrorBoundary';
import Stake from './components/Staking/Stake';
import StakingPositions from './components/Staking/StakingPositions';
import CashSwap from './components/Swap/CashSwap';
import Mining from './components/Mining/Mining'; 
import LPStakingInterface from './components/LPStaking/LPStakingInterface'; 
import enterImage from './assets/enter.png';

// Define your MUI theme
const theme = createTheme({
  palette: {
    primary: { main: '#ffcc00' },
    secondary: { main: '#1c1c1c' },
  },
  typography: {
    fontFamily: 'Staatliches, sans-serif',
  },
});

const LoadingComponent = memo(({ message }) => (
  <Box className="loading">
    <CircularProgress />
    <Typography variant="h6" sx={{ mt: 2 }}>
      {message}
    </Typography>
  </Box>
));

// Hype "Tick Tock" Banner component
function TickTockBanner() {
  const [tick, setTick] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => !prev);
    }, 1000); // Toggle every second

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#000000",
        color: "#ffcc00",
        padding: "12px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: "1.2rem",
        position: "sticky",
        top: 0,
        zIndex: 12000,
        borderBottom: "2px solid #ffcc00",
        textTransform: "uppercase",
        letterSpacing: "3px",
        fontFamily: "'Courier New', monospace",
        textShadow: "0 0 10px rgba(255, 204, 0, 0.5)",
        transition: "all 0.3s ease",
      }}
    >
      <Typography 
        sx={{ 
          flexGrow: 1, 
          textAlign: "center",
          animation: "pulse 2s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.7 }
          }
        }}
      >
        <span style={{ 
          color: tick ? "#ffcc00" : "#ff0000",
          transition: "color 0.3s ease",
          fontWeight: "900"
        }}>
          TICK TOCK
        </span>
        {" "}⏰{" "}
        <span style={{ 
          color: "#ffffff",
          fontSize: "0.9rem"
        }}>
          SOMETHING IS COMING...
        </span>
      </Typography>
    </Box>
  );
}

function AppContent() {
  // Removed miningEventStatus since it is not used.
  const { isInitialLoading } = useContext(MiningContext);
  const { account } = useContext(WalletContext);
  const [stakingTab, setStakingTab] = useState(0); // 0 for CASH staking, 1 for LP staking

  const handleStakingTabChange = (event, newValue) => {
    setStakingTab(newValue);
  };

  const matrixChars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲンヴ▓░█▒▚▞◢◣◤◥⌘⌦⌫∆∇∏∑∕∙≈≠≤≥⊕⊗⊢⊣⊤⊥⋄⋆⋈⋉⋊⋋⋌⋍⋎⋏⋐⋑⋒⋓⋔⋕⋖⋗⋘⋙⋚⋛⋜⋝⋞⋟';

  const glitchInterval = useRef(null);

  useEffect(() => {
    const chars = document.querySelectorAll('.char');
  
    const glitchChar = (char) => {
      const originalChar = char.getAttribute('data-char');
      const shouldGlitch = Math.random() < 0.2; 
      
      if (shouldGlitch) {
        const numTransforms = Math.floor(Math.random() * 3) + 1;
        let delay = 0;
        
        for (let i = 0; i < numTransforms; i++) {
          setTimeout(() => {
            const randomChar = matrixChars[Math.floor(Math.random() * matrixChars.length)];
            char.textContent = randomChar;
            const colors = ['#00ff00', '#ff0000', '#0000ff', '#ffff00', '#00ffff'];
            char.style.color = colors[Math.floor(Math.random() * colors.length)];
            char.style.textShadow = `0 0 5px ${char.style.color}`;
            const skewX = (Math.random() - 0.5) * 20;
            const skewY = (Math.random() - 0.5) * 20;
            char.style.transform = `skew(${skewX}deg, ${skewY}deg)`;
          }, delay);
          delay += Math.random() * 50 + 25;
        }
        
        setTimeout(() => {
          char.textContent = originalChar;
          char.style.color = '';
          char.style.textShadow = '';
          char.style.transform = '';
        }, delay + 50);
      }
    };

    const startGlitchLoop = () => {
      const randomInterval = Math.random() * 1000 + 500; 
      glitchInterval.current = setTimeout(() => {
        chars.forEach(char => {
          setTimeout(() => glitchChar(char), Math.random() * 200);
        });
        startGlitchLoop();
      }, randomInterval);
    };

    startGlitchLoop();

    return () => {
      if (glitchInterval.current) {
        clearTimeout(glitchInterval.current);
      }
    };
  }, [matrixChars]);

  const isDataLoading = isInitialLoading;

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        {/* Hype "Tick Tock" banner at the very top */}
        <TickTockBanner />

        <Header />

        {isDataLoading && (
          <Box className="global-loading-overlay">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading application data...
            </Typography>
          </Box>
        )}

        {!account ? (
          <section id="buy-cash" className="cash-buy-section section-style connect-wallet-section">
            <Box className="connect-wallet-container">
              <img src={enterImage} alt="Connect Wallet to enter dApp" className="enter-image" />
              <Typography variant="h5" className="connect-wallet-text text-center text-uppercase">
                <span className="char" data-char="$">$</span>
                <span className="char" data-char="W">W</span>
                <span className="char" data-char="e">e</span>
                <span className="char" data-char="l">l</span>
                <span className="char" data-char="c">c</span>
                <span className="char" data-char="o">o</span>
                <span className="char" data-char="m">m</span>
                <span className="char" data-char="e">e</span>
                <span className="char" data-char=" ">&nbsp;</span>
                <span className="char" data-char="t">t</span>
                <span className="char" data-char="o">o</span>
                <span className="char" data-char=" ">&nbsp;</span>
                <span className="char revolution-char" data-char="T">T</span>
                <span className="char revolution-char" data-char="h">h</span>
                <span className="char revolution-char" data-char="e">e</span>
                <span className="char" data-char=" ">&nbsp;</span>
                <span className="char revolution-char" data-char="R">R</span>
                <span className="char revolution-char" data-char="e">e</span>
                <span className="char revolution-char" data-char="v">v</span>
                <span className="char revolution-char" data-char="o">o</span>
                <span className="char revolution-char" data-char="l">l</span>
                <span className="char revolution-char" data-char="u">u</span>
                <span className="char revolution-char" data-char="t">t</span>
                <span className="char revolution-char" data-char="i">i</span>
                <span className="char revolution-char" data-char="o">o</span>
                <span className="char revolution-char" data-char="n">n</span>
                <span className="char" data-char=" ">&nbsp;</span>
                <span className="char" data-char="o">o</span>
                <span className="char" data-char="f">f</span>
                <span className="char" data-char=" ">&nbsp;</span>
                <span className="char" data-char="C">C</span>
                <span className="char" data-char="u">u</span>
                <span className="char" data-char="r">r</span>
                <span className="char" data-char="r">r</span>
                <span className="char" data-char="e">e</span>
                <span className="char" data-char="n">n</span>
                <span className="char" data-char="c">c</span>
                <span className="char" data-char="y">y</span>
                <span className="char" data-char="$">$</span>
              </Typography>
            </Box>
          </section>
        ) : (
          <section id="mining" className="mining-section section-style">
            <Suspense fallback={<LoadingComponent message="Loading Mining Component..." />}>
              <Mining />
            </Suspense>
          </section>
        )}

        {/* Cash Swap & Staking Sections always open */}
        <section id="buy-cash" className="cash-buy-section section-style">
          <Suspense fallback={<LoadingComponent message="Loading Cash Swap..." />}>
            <CashSwap />
          </Suspense>
        </section>

        <section id="staking" className="staking-section-container section-style">
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={stakingTab} 
              onChange={handleStakingTabChange}
              centered
              sx={{ 
                '& .MuiTab-root': { 
                  color: '#ffcc00', 
                  '&.Mui-selected': { color: '#ffffff' } 
                },
                '& .MuiTabs-indicator': { backgroundColor: '#ffcc00' }
              }}
            >
              <Tab label="CASH STAKING" id="staking-tab-0" aria-controls="staking-tabpanel-0" />
              <Tab label="LP STAKING" id="staking-tab-1" aria-controls="staking-tabpanel-1" />
            </Tabs>
          </Box>
          
          <Box
            role="tabpanel"
            hidden={stakingTab !== 0}
            id="staking-tabpanel-0"
            aria-labelledby="staking-tab-0"
          >
            {stakingTab === 0 && (
              <Suspense fallback={<LoadingComponent message="Loading Cash Staking..." />}>
                <Stake stakingPeriodLabels={['30 Days', '60 Days', '90 Days', '180 Days']} />
              </Suspense>
            )}
          </Box>
          
          <Box
            role="tabpanel"
            hidden={stakingTab !== 1}
            id="staking-tabpanel-1"
            aria-labelledby="staking-tab-1"
          >
            {stakingTab === 1 && (
              <Suspense fallback={<LoadingComponent message="Loading LP Staking..." />}>
                <LPStakingInterface />
              </Suspense>
            )}
          </Box>
        </section>

        <section id="staking-positions" className="staking-positions-section section-style">
          <Suspense fallback={<LoadingComponent message="Loading Staking Positions..." />}>
            <StakingPositions stakingPeriodLabels={['30 Days', '60 Days', '90 Days', '180 Days']} />
          </Suspense>
        </section>

        <Footer />
      </ThemeProvider>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <MainProvider>
        <AppContent />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          toastClassName="dark-toast"
          bodyClassName="dark-toast-body"
        />
      </MainProvider>
    </ErrorBoundary>
  );
}